
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { MessageSquare, Trash2, Sparkle } from "lucide-react";
import { Review } from "@/types/reviews";
import { Badge } from "@/components/ui/badge";

interface DebugReviewDetailDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedReview: Review | null;
  onDelete: (id: number) => void;
  onHighlightToggle?: (id: number, highlighted: boolean) => void;
}

export const DebugReviewDetailDialog = ({
  isOpen,
  onOpenChange,
  selectedReview,
  onDelete,
  onHighlightToggle,
}: DebugReviewDetailDialogProps) => {
  if (!selectedReview) return null;

  // Log to debug the review details
  console.log("[DEBUG] Review in dialog:", {
    id: selectedReview.id,
    highlighted: selectedReview.highlighted,
    type: typeof selectedReview.highlighted
  });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Review Details (Debug Mode)
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-[100px_1fr] gap-1">
            <h3 className="font-semibold text-gray-500">ID:</h3>
            <p className="font-mono text-sm">{selectedReview.id}</p>
          </div>
          
          <div className="grid grid-cols-[100px_1fr] gap-1">
            <h3 className="font-semibold text-gray-500">Service:</h3>
            <p>{selectedReview.service_name || "Unknown"}</p>
          </div>
          
          <div className="grid grid-cols-[100px_1fr] gap-1">
            <h3 className="font-semibold text-gray-500">Date:</h3>
            <p>{new Date(selectedReview.created_at).toLocaleString()}</p>
          </div>
          
          <div className="grid grid-cols-[100px_1fr] gap-1">
            <h3 className="font-semibold text-gray-500">Rating:</h3>
            <p>{selectedReview.rating} / 5</p>
          </div>
          
          <div className="grid grid-cols-[100px_1fr] gap-1">
            <h3 className="font-semibold text-gray-500">Highlighted:</h3>
            <div className="flex items-center gap-2">
              <Badge variant={selectedReview.highlighted ? "secondary" : "outline"}>
                {selectedReview.highlighted === true ? "Yes" : "No"}
              </Badge>
              <span className="text-xs text-gray-500">
                (type: {typeof selectedReview.highlighted})
              </span>
            </div>
          </div>
          
          {onHighlightToggle && (
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox 
                id="highlight-review-debug" 
                checked={selectedReview.highlighted === true}
                onCheckedChange={(checked) => {
                  if (typeof checked === "boolean") {
                    console.log("[DEBUG] Highlight checkbox changed to:", checked);
                    onHighlightToggle(selectedReview.id, checked);
                  }
                }}
                className="data-[state=checked]:bg-yellow-400 data-[state=checked]:text-white data-[state=checked]:border-yellow-400"
              />
              <label
                htmlFor="highlight-review-debug"
                className="text-sm font-medium flex items-center gap-1 cursor-pointer"
              >
                <Sparkle className="h-4 w-4 text-yellow-400" />
                Highlight this review on service page
              </label>
            </div>
          )}
          
          <div>
            <h3 className="font-semibold text-gray-500 mb-2">Comment:</h3>
            <div className="bg-gray-50 p-3 rounded-md whitespace-pre-wrap">
              {selectedReview.comment || "No comment provided"}
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-500 mb-2">Raw Data:</h3>
            <pre className="bg-gray-900 text-gray-100 p-3 rounded-md text-xs overflow-auto max-h-40">
              {JSON.stringify(selectedReview, null, 2)}
            </pre>
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="destructive"
              onClick={() => onDelete(selectedReview.id)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Review
            </Button>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
